import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import { ChartBarIcon, BookOpenIcon } from "@heroicons/react/outline";
import moment from "moment";
import { toast } from "react-toastify";
import exportShortTermBillingExcel from "../../utils/excelExportShortTermBilling";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ETB",
    minimumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatSignedCurrency = (amount) => {
  const n = Number(amount) || 0;
  const abs = Math.abs(n);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "ETB",
    minimumFractionDigits: 2,
    signDisplay: "never",
  }).format(abs);
  if (n === 0) return formatted;
  return `${n < 0 ? "−" : "+"}${formatted}`;
};

const bankLabel = (bank) => {
  if (bank === "short-term-credit") return "Short-term credit";
  if (!bank) return "—";
  return String(bank).toUpperCase();
};

const billingKind = (row) => {
  if (row.__billingType === "credit") return "credit";
  if (row.__billingType === "settlement") return "settlement";
  if (row.type === "STCreditSettlement") return "settlement";
  if (row.type === "deposit" && row.bank === "short-term-credit")
    return "credit";
  return null;
};

const typeLabel = (row) => {
  const k = billingKind(row);
  if (k === "credit") return "Credit";
  if (k === "settlement") return "Settlement";
  return row.type || "—";
};

const typeBadgeClass = (row) => {
  const k = billingKind(row);
  if (k === "credit") return "bg-emerald-100 text-emerald-800";
  if (k === "settlement") return "bg-amber-100 text-amber-900";
  return "bg-gray-100 text-gray-800";
};

/** Gross credit amount (matches historical totals). */
const rowCreditGross = (row) =>
  (Number(row.deposit) || 0) +
  (Number(row.amount) || 0) +
  (Number(row.serviceCharge) || 0);

/** Ledger movement: credit (−), settlement (+). */
const signedLedgerMovement = (row) => {
  const k = billingKind(row);
  if (k === "credit") return -rowCreditGross(row);
  if (k === "settlement") return Number(row.deposit) || 0;
  return 0;
};

const movementCellClass = (movement) => {
  if (movement < 0) return "text-emerald-800";
  if (movement > 0) return "text-amber-900";
  return "text-gray-600";
};

const OtherBillingTable = ({ rows, emptyLabel }) => {
  if (rows.length === 0) return null;

  return (
    <section className="flex overflow-hidden flex-col gap-3 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex flex-wrap gap-3 justify-between items-center px-4 py-3 text-white bg-gray-600">
        <div>
          <h3 className="text-base font-bold">Other transactions</h3>
          <p className="text-xs opacity-90 sm:text-sm">
            Entries not classified as credit or settlement
          </p>
        </div>
        <p className="font-mono text-lg font-bold tabular-nums">
          {formatCurrency(
            rows.reduce((s, r) => s + (Number(r.deposit) || 0), 0)
          )}
        </p>
      </div>

      <div className="max-h-[min(40vh,20rem)] overflow-auto">
        <table className="w-full text-sm text-left text-gray-800 border-collapse">
          <thead className="sticky top-0 z-[1] border-b border-gray-200 bg-gray-100">
            <tr>
              <th className="px-3 py-2.5 font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2.5 font-semibold text-gray-700">Date</th>
              <th className="px-3 py-2.5 text-right font-semibold text-gray-700">
                Amount
              </th>
              <th className="px-3 py-2.5 font-semibold text-gray-700">
                Bank / channel
              </th>
              <th className="min-w-[8rem] px-3 py-2.5 font-semibold text-gray-700">
                Reference
              </th>
              <th className="px-3 py-2.5 font-semibold text-gray-700">
                Approved by
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row._id ?? idx}
                className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/80"}
              >
                <td className="px-3 py-2 align-middle border-b border-gray-100">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${typeBadgeClass(
                      row
                    )}`}
                  >
                    {typeLabel(row)}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-600 align-middle whitespace-nowrap border-b border-gray-100">
                  {moment(row.createdAt).format("DD MMM YYYY HH:mm")}
                </td>
                <td className="px-3 py-2 font-mono font-semibold tabular-nums text-right text-gray-900 align-middle border-b border-gray-100">
                  {formatCurrency(row.deposit)}
                </td>
                <td className="px-3 py-2 align-middle border-b border-gray-100">
                  {bankLabel(row.bank)}
                </td>
                <td
                  className="max-w-[14rem] truncate border-b border-gray-100 px-3 py-2 align-middle font-mono text-xs text-gray-700"
                  title={row.transaction || undefined}
                >
                  {row.transaction || "—"}
                </td>
                <td className="px-3 py-2 text-gray-700 align-middle border-b border-gray-100">
                  {row.approvedBy || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const ShortTermBillingModal = ({
  show,
  onHide,
  shortTermBillings,
  companyName,
}) => {
  const {
    ledger,
    other,
    creditTotal,
    settlementTotal,
    closingBalance,
    creditsCount,
    settlementsCount,
  } = useMemo(() => {
    const items = Array.isArray(shortTermBillings) ? shortTermBillings : [];
    const byDateAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

    const cred = items.filter((r) => billingKind(r) === "credit");
    const sett = items.filter((r) => billingKind(r) === "settlement");
    const rest = items.filter((r) => !billingKind(r));

    const ledgerSource = [...cred, ...sett].sort(byDateAsc);
    let running = 0;
    const ledger = ledgerSource.map((row) => {
      const previousBalance = running;
      const movement = signedLedgerMovement(row);
      running = previousBalance + movement;
      return { row, previousBalance, movement, newBalance: running };
    });

    const creditTotal = cred.reduce((s, r) => s + rowCreditGross(r), 0);
    const settlementTotal = sett.reduce(
      (s, r) => s + (Number(r.deposit) || 0),
      0
    );

    return {
      ledger,
      other: rest,
      creditTotal,
      settlementTotal,
      closingBalance: running,
      creditsCount: cred.length,
      settlementsCount: sett.length,
    };
  }, [shortTermBillings]);

  const buildLedgerExcelRows = () =>
    ledger.map((entry, index) => ({
      "S/N": index + 1,
      Type: typeLabel(entry.row),
      Date: moment(entry.row.createdAt).format("DD-MM-YYYY HH:mm"),
      "Previous balance": entry.previousBalance,
      Movement: entry.movement,
      "New balance": entry.newBalance,
      "Bank / channel": bankLabel(entry.row.bank),
      Reference: entry.row.transaction || "",
      "Approved by": entry.row.approvedBy || "",
      Category: "",
    }));

  const safeExportBase = () =>
    (companyName || "Short-term-billing").replace(/[/\\?%*:|"<>]/g, "-");

  const handleExportLedgerExcel = () => {
    if (ledger.length === 0) {
      toast.error("No ledger entries to export.");
      return;
    }
    const date = moment().format("DD-MM-YYYY");
    const rows = buildLedgerExcelRows();
    rows.push({
      "S/N": "",
      Type: "Closing balance",
      Date: "",
      "Previous balance": "",
      Movement: "",
      "New balance": closingBalance,
      "Bank / channel": "",
      Reference: "",
      "Approved by": "",
      Category: "GRAND TOTAL",
    });
    exportShortTermBillingExcel(
      rows,
      `${safeExportBase()}-ShortTermLedger-${date}`,
      "Ledger"
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="short-term-billing-modal"
      className="financial-dashboard"
    >
      <Modal.Header closeButton className="text-black border-0">
        <Modal.Title
          className="flex gap-3 items-center text-2xl font-bold"
          id="short-term-billing-modal"
        >
          <ChartBarIcon className="w-8 h-8" />
          Short-term billing ledger
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-gray-50 sm:p-4">
        <div className="flex flex-col gap-8">
          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-wrap gap-3 justify-between items-start mb-4">
              <div className="flex gap-3 items-start">
                <BookOpenIcon className="w-8 h-8 text-slate-700 shrink-0" />
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Running balance
                  </h3>
                  <p className="text-sm text-gray-600">
                    Credits reduce the balance (−); settlements increase it (+).
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <div className="px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs font-medium text-emerald-900">
                    Credit (−)
                  </p>
                  <p className="font-mono font-semibold tabular-nums text-emerald-800">
                    {formatSignedCurrency(-creditTotal)}
                  </p>
                  <p className="text-xs text-emerald-800/80">
                    {creditsCount} entr{creditsCount === 1 ? "y" : "ies"}
                  </p>
                </div>
                <div className="px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-medium text-amber-900">
                    Settlement (+)
                  </p>
                  <p className="font-mono font-semibold tabular-nums text-amber-950">
                    {formatSignedCurrency(settlementTotal)}
                  </p>
                  <p className="text-xs text-amber-900/80">
                    {settlementsCount} entr
                    {settlementsCount === 1 ? "y" : "ies"}
                  </p>
                </div>
                <div className="px-3 py-2 rounded-lg border bg-slate-100 border-slate-200">
                  <p className="text-xs font-medium text-slate-800">
                    Closing balance
                  </p>
                  <p className="font-mono text-lg font-bold tabular-nums text-slate-900">
                    {formatCurrency(closingBalance)}
                  </p>
                </div>
              </div>
            </div>

            {ledger.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                No credit or settlement entries in this period.
              </p>
            ) : (
              <div className="max-h-[min(55vh,32rem)] overflow-auto rounded-lg border border-gray-100">
                <table className="w-full min-w-[56rem] text-sm text-left text-gray-800 border-collapse">
                  <thead className="sticky top-0 z-[1] border-b border-gray-200 bg-slate-100">
                    <tr>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                        Type
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                        Date
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-right text-gray-700 whitespace-nowrap">
                        Previous balance
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-right text-gray-700 whitespace-nowrap">
                        Movement
                        <span className="block text-xs font-normal text-gray-500">
                          credit − / settlement +
                        </span>
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-right text-gray-700 whitespace-nowrap">
                        New balance
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                        Bank / channel
                      </th>
                      <th className="min-w-[7rem] px-3 py-2.5 font-semibold text-gray-700">
                        Reference
                      </th>
                      <th className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                        Approved by
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((entry, idx) => (
                      <tr
                        key={entry.row._id ?? idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/80"}
                      >
                        <td className="px-3 py-2 align-middle whitespace-nowrap border-b border-gray-100">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${typeBadgeClass(
                              entry.row
                            )}`}
                          >
                            {typeLabel(entry.row)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 align-middle whitespace-nowrap border-b border-gray-100">
                          {moment(entry.row.createdAt).format(
                            "DD MMM YYYY HH:mm"
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums text-right text-gray-800 align-middle border-b border-gray-100">
                          {formatCurrency(entry.previousBalance)}
                        </td>
                        <td
                          className={`px-3 py-2 font-mono font-semibold text-right tabular-nums align-middle border-b border-gray-100 ${movementCellClass(
                            entry.movement
                          )}`}
                        >
                          {formatSignedCurrency(entry.movement)}
                        </td>
                        <td className="px-3 py-2 font-mono font-semibold tabular-nums text-right text-gray-900 align-middle border-b border-gray-100">
                          {formatCurrency(entry.newBalance)}
                        </td>
                        <td className="px-3 py-2 align-middle border-b border-gray-100">
                          {bankLabel(entry.row.bank)}
                        </td>
                        <td
                          className="max-w-[12rem] truncate border-b border-gray-100 px-3 py-2 align-middle font-mono text-xs text-gray-700"
                          title={entry.row.transaction || undefined}
                        >
                          {entry.row.transaction || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-700 align-middle border-b border-gray-100">
                          {entry.row.approvedBy || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {other.length > 0 ? <OtherBillingTable rows={other} /> : null}
        </div>
      </Modal.Body>

      <Modal.Footer className="flex flex-wrap gap-2 bg-white border-t border-gray-200">
        <button
          type="button"
          onClick={handleExportLedgerExcel}
          className="px-6 py-2 font-medium text-white rounded-lg transition-colors bg-slate-700 hover:bg-slate-800"
        >
          <i className="far fa-file-excel me-2" aria-hidden />
          Export ledger (Excel)
        </button>
        <button
          type="button"
          onClick={onHide}
          className="px-6 py-2 font-medium text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShortTermBillingModal;
